import React from 'react';

import { onAutocompleteChangeInForm } from '@sharedCR/common/form';
import { ModalSize } from '@sharedCR/coreDesignSystem/Modals';
import { applicationFeesApi } from 'app/services/api';
import FeeProviderForm from 'app/shared/modules/FeeProviderForm';
import _ from 'lodash';

import { DISCOUNT_POINTS_FEE, HARDCODED_FEES, recordingTypeOptions } from 'constants/feeTypes';
import { getAllowedCategoryIds } from 'utils/feeTypes';
import { caseInsensitiveIncludes } from 'utils/string';

import { closeDialogAction, openDialogAction } from 'store/global/actions';

import { ADD_NEW_FEE_PROVIDER } from './FeeTable.constants';
import SubProviderConfirmDialog from './SubProviderConfirmDialog';

export const getExtraFeeProps = ({
  feeTypeId,
  feeTypeName = '',
  borrowerPaidAtClosingAmount,
  borrowerPaidBeforeClosingAmount,
  sellerPaidAtClosingAmount,
  sellerPaidBeforeClosingAmount,
  paidByOthersAmount,
  isManageDiscountPointsAllowed,
}) => {
  const isDiscountPointsFee = caseInsensitiveIncludes(feeTypeName.toString(), DISCOUNT_POINTS_FEE);
  const isHardcodedFee = _.values(HARDCODED_FEES).some(
    fee => feeTypeName.toString().trim().toLowerCase() === fee.trim().toLowerCase(),
  );

  const isDiscountPointsReadonly = !isManageDiscountPointsAllowed && isDiscountPointsFee;

  const isEveryAmountEmpty = [
    borrowerPaidAtClosingAmount,
    borrowerPaidBeforeClosingAmount,
    sellerPaidAtClosingAmount,
    sellerPaidBeforeClosingAmount,
    paidByOthersAmount,
  ].every(x => !x);

  const allowedCategoryIds = getAllowedCategoryIds(feeTypeId);

  const onFilterOptions = (options, params) => {
    const { inputValue } = params;

    return [
      ...options.filter(option => {
        const isSelectedProviderCategory = allowedCategoryIds.includes(
          option.feeProviderCategoryId,
        );

        return (
          isSelectedProviderCategory &&
          (!inputValue || caseInsensitiveIncludes(option.name, inputValue))
        );
      }),
    ];
  };

  return {
    isHardcodedFee,
    isDiscountPointsReadonly,
    isEveryAmountEmpty,
    onFilterOptions,
  };
};

export const feeTypeNameChange =
  ({ validateField, setInForm, setForm, prefix, index }) =>
  (e, value) => {
    onAutocompleteChangeInForm({ validateField, setInForm })(e, value);

    setForm(prev => {
      const clonePrev = _.cloneDeep(prev);
      clonePrev[prefix][index].feeTypeName = value?.name;
      clonePrev[prefix][index].feeProviderId = null;
      clonePrev[prefix][index].recordingType = recordingTypeOptions.find(
        i => i.value === value?.recordingType,
      );

      return clonePrev;
    });
  };

const applyProviderChange = ({
  setInForm,
  validateField,
  setForm,
  prefix,
  index,
  fieldName,
  option,
  subProviderOfFeeProviderId = null,
}) => {
  setInForm(fieldName, option || '');

  if (validateField) {
    validateField(fieldName, option || '');
  }

  if (subProviderOfFeeProviderId) {
    setForm(prev => {
      const clonePrev = _.cloneDeep(prev);

      clonePrev[prefix][index].subProviderOfFeeProviderId = subProviderOfFeeProviderId;

      return clonePrev;
    });
  }
};

export const feeProviderChange =
  ({
    dispatch,
    setInForm,
    validateField,
    prefix,
    setForm,
    setIsTouched,
    index,
    loanId,
    isOverrideSubProviderAllowed,
    feeTypeId,
    currentFeeProviderId,
  }) =>
  async (e, option) => {
    if (option?.value === ADD_NEW_FEE_PROVIDER) {
      const onSuccessCreate = payload => {
        dispatch(closeDialogAction());
        setIsTouched(true);
        setForm(prev => {
          const clonePrev = _.cloneDeep(prev);
          clonePrev[prefix][index].feeProviderId = {
            name: payload.name,
            value: payload.feeProviderId,
          };

          return clonePrev;
        });
      };

      return dispatch(
        openDialogAction({
          header: 'Add Fee Provider',
          isRestyled: true,
          size: ModalSize.LARGE,
          withDivider: true,
          hasScrollY: false,
          body: dialogProps => (
            <FeeProviderForm
              isDialog
              feeProviderCategoryId={option.feeProviderCategoryId}
              isLocationsIncluded={false}
              onSuccessCreate={onSuccessCreate}
              {...dialogProps}
            />
          ),
        }),
      );
    }

    // Extract field name from the event target before any async work,
    // because React synthetic events are recycled and e.currentTarget
    // becomes null after an await.
    const target =
      e.currentTarget?.querySelector?.('[data-fieldname]') ||
      e.currentTarget?.querySelector?.('[name]') ||
      e.currentTarget;
    const fieldName =
      target?.dataset?.fieldname || target?.name || `${prefix}.[${index}].feeProviderId`;

    const feeTypeIdValue = feeTypeId?.value || feeTypeId;

    if (!isOverrideSubProviderAllowed || !option?.value || !loanId || !feeTypeIdValue) {
      setInForm(fieldName, option || '');

      if (validateField) {
        validateField(fieldName, option || '');
      }

      return;
    }

    try {
      const { data } = await applicationFeesApi.evaluateSectionChange({
        loanId,
        feeTypeId: feeTypeIdValue,
        newProviderId: option.value,
      });

      if (!data?.wouldMoveToC) {
        setInForm(fieldName, option || '');

        if (validateField) {
          validateField(fieldName, option || '');
        }

        return;
      }

      const onDecline = () => {
        dispatch(closeDialogAction());
        applyProviderChange({
          setInForm,
          validateField,
          setForm,
          prefix,
          index,
          fieldName,
          option,
        });
      };

      const onConfirm = () => {
        dispatch(closeDialogAction());
        setIsTouched(true);
        applyProviderChange({
          setInForm,
          validateField,
          setForm,
          prefix,
          index,
          fieldName,
          option,
          subProviderOfFeeProviderId: currentFeeProviderId,
        });
      };

      dispatch(
        openDialogAction({
          header: 'Sub-Provider Confirmation',
          isRestyled: true,
          isDisableClosing: true,
          size: ModalSize.SMALL,
          withDivider: true,
          hasScrollY: false,
          body: () => (
            <SubProviderConfirmDialog
              newProviderName={option.name}
              originalProviderName={data.originalProviderName}
              onConfirm={onConfirm}
              onDecline={onDecline}
            />
          ),
        }),
      );
    } catch {
      setInForm(fieldName, option || '');

      if (validateField) {
        validateField(fieldName, option || '');
      }
    }
  };
